import { useEffect, useRef } from 'react';

import { GLOBAL } from 'saddlebag-browser';

const useOnScreen = (logEventFn: () => void, customOptions?: IntersectionObserverInit) => {
  // Create a ref to the element to observe
  const ref = useRef(null);

  // Keep track of whether the element has been visible at least once
  const hasBeenVisibleRef = useRef(false);

  useEffect(() => {
    const currentRef = ref.current;
    const $window = GLOBAL.getWindow();
    const options = {
      threshold: 0.5, // 50% of the element should be visible
      ...customOptions,
    };

    const checkDisplay = (element: Element) =>
      $window && $window.getComputedStyle(element).display !== 'none';

    let observer: IntersectionObserver;

    if ($window && typeof $window.IntersectionObserver === 'function') {
      observer = new $window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) =>
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= (options.threshold as number) && entry.isIntersecting) {
              // If the element is visible and has not been visible before, log the event
              if (!hasBeenVisibleRef.current) {
                logEventFn();
                hasBeenVisibleRef.current = true;
              }
            }
          }),
        options,
      );

      // Start observing the element
      if (currentRef && checkDisplay(currentRef)) {
        observer.observe(currentRef);
      }

      if (currentRef && !checkDisplay(currentRef)) {
        observer.unobserve(currentRef);
      }
    }

    // Clean up the observer when the component unmounts
    return () => {
      if (currentRef && observer) {
        observer.unobserve(currentRef);
        observer.disconnect();
      }
    };
  }, [customOptions, logEventFn]);

  return [ref];
};

export default useOnScreen;
